import React, { useState } from "react";
import { UseFormRegister, FieldErrors } from "react-hook-form";
import { useTranslation } from "react-i18next";
import { Pencil, X } from "lucide-react";
import InputField from "../../form/InputField";
import { PermissionGate } from "../PermissionGate";
import { PermissionsKey } from "../../../data/permissions";

export interface PointsSettingsFormData {
  pointsPerRiyal: number;
  riyalValue: number;
  inviteFriendPoints: number;
}

interface Props {
  register: UseFormRegister<PointsSettingsFormData>;
  errors: FieldErrors<PointsSettingsFormData>;
  defaultValues?: Partial<PointsSettingsFormData>;
  onSave?: () => void;
}

function PointsSettingsForm({ register, errors, defaultValues, onSave }: Props) {
  const { t } = useTranslation();
  const [isEditing, setIsEditing] = useState(false);

  const handleToggleEdit = () => {
    setIsEditing((prev) => !prev);
  };

  return (
    <div className="space-y-6">
      {/* Header with Edit button */}
      <div className="flex items-center justify-between">
        <div>
          <h3 className="text-lg font-semibold">{t("points_settings")}</h3>
          {!isEditing && (
            <p className="text-sm text-gray-500 mt-1">
              {t("click_edit_to_update_values")}
            </p>
          )}
        </div>

        <PermissionGate permissions={[PermissionsKey.SystemPoints]}>
            <button
              type="button"
              onClick={handleToggleEdit}
              className={`flex items-center gap-2 px-3 py-1.5 text-sm rounded-md transition font-medium ${
                isEditing
                  ? "bg-gray-100 text-gray-700 hover:bg-gray-200"
                  : "bg-primary text-white hover:opacity-90"
              }`}
            >
              {isEditing ? (
                <>
                  <X size={16} /> {t("cancel_edit")}
                </>
              ) : (
                <>
                  <Pencil size={16} /> {t("edit")}
                </>
              )}
            </button>
        </PermissionGate>
      </div>

      {/* Points value per Riyal */}
      <div className="flex items-center gap-2">
        <div className="flex-1">
          <InputField
            label={t("points")}
            type="number"
            placeholder=""
            readOnly={!isEditing}
            defaultValue={defaultValues?.pointsPerRiyal || ""}
            register={register("pointsPerRiyal", {
              required: t("required_field") as string,
              valueAsNumber: true,
            })}
            error={errors.pointsPerRiyal}
          />
        </div>

        <span className="text-xl font-semibold">=</span>

        <div className="flex-1">
          <InputField
            label={t("riyal_value")}
            type="number"
            placeholder=""
            readOnly={!isEditing}
            defaultValue={defaultValues?.riyalValue || ""}
            register={register("riyalValue", {
              required: t("required_field") as string,
              valueAsNumber: true,
            })}
            error={errors.riyalValue}
          />
        </div>
      </div>

      {/* Points for invite friend */}
      <InputField
        label={t("invite_friend_points")}
        type="number"
        placeholder=""
        readOnly={!isEditing}
        defaultValue={defaultValues?.inviteFriendPoints || ""}
        register={register("inviteFriendPoints", {
          required: t("required_field") as string,
          valueAsNumber: true,
        })}
        error={errors.inviteFriendPoints}
      />

      {/* Submit button only when editing */}
      {/* {isEditing && (
        <div className="flex pt-2">
        {
                false?
                <LoadingButton /> 
                :
                <SubmitButton  label="update" />
                }
        </div>
      )} */}
    </div>
  );
}

export default PointsSettingsForm;
